import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, describeError } from '@/lib/api';
import { useIdempotencyKey } from '@/lib/idempotency';
import { useStepUpDialog, type StepUpProof } from '@/lib/step-up';
import { stepUpTarget } from '@/lib/passkeys';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import {
  AGENT_WORK_ORDERS_BATCH_URL,
  cancelAgentWorkOrder,
  createAgentWorkOrderBatch,
  describeWorkOrderError,
  getAgentWorkOrderOptions,
  isFormValid,
  kindLabel,
  listAgentWorkOrders,
  minimumForm,
  type AgentWorkOrderDto,
  type AgentWorkOrderForm,
  type AgentWorkOrderOptions,
} from '@/lib/agent-work-orders';
import { AgentWorkOrderFields, AgentWorkOrderTable } from '@/components/AgentWorkOrderFields';
import { StepUpDialog } from '@/components/StepUpDialog';
import { CardTable, TBody, THead, Td, Th, Tr } from '@/components/CardTable';
import { subscriptionStatusLabel, subscriptionStatusOptions } from '@/components/badge-labels';

interface TenantRow {
  id: string;
  tradeName: string;
  slug: string;
  isActive: boolean;
  subscriptionStatus: string;
  trialExpiresAt: string | null;
}

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  ...subscriptionStatusOptions(['Trial', 'Expired', 'PastDue']),
  { value: '', label: 'Todos' },
];

type BatchPayload = { tenantIds: string[]; form: AgentWorkOrderForm };

/**
 * "Lojas para agir" (change platform-mcp-writes, tarefa 1.17): até 10 ordens de serviço de uma vez,
 * uma por loja marcada, com um só step-up. As lojas vêm das linhas marcadas (o `id` de cada uma),
 * nunca de nome digitado. Só os tipos que podem sair em lote: dinheiro e perda de acesso ficam para
 * a página da loja.
 */
export function AgentWorkOrdersBatchPage() {
  const [options, setOptions] = useState<AgentWorkOrderOptions | null>(null);
  const [form, setForm] = useState<AgentWorkOrderForm | null>(null);
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [selected, setSelected] = useState<TenantRow[]>([]);
  const [status, setStatus] = useState('Trial');
  const [q, setQ] = useState('');
  const debouncedQ = useDebouncedValue(q, 400);
  const [recent, setRecent] = useState<AgentWorkOrderDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const idempotency = useIdempotencyKey();

  const loadRecent = useCallback(async () => {
    setRecent(await listAgentWorkOrders());
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getAgentWorkOrderOptions(), listAgentWorkOrders()])
      .then(([loadedOptions, loadedRecent]) => {
        if (cancelled) return;
        const batchOptions = { ...loadedOptions, kinds: loadedOptions.kinds.filter((k) => k.batchable) };
        setOptions(batchOptions);
        setForm(minimumForm(batchOptions));
        setRecent(loadedRecent);
      })
      .catch((err) => !cancelled && setError(describeError(err)));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ items: TenantRow[] }>('/platform/tenants', {
        params: { subscriptionStatus: status || undefined, isActive: true, q: debouncedQ || undefined, page: 1, pageSize: 20 },
      })
      .then(({ data }) => !cancelled && setTenants(data.items))
      .catch((err) => !cancelled && setError(describeError(err)));
    return () => { cancelled = true; };
  }, [status, debouncedQ]);

  const maxBatch = options?.maxBatchSize ?? 10;

  function toggle(tenant: TenantRow) {
    setSelected((current) => {
      if (current.some((t) => t.id === tenant.id)) return current.filter((t) => t.id !== tenant.id);
      if (current.length >= maxBatch) return current;
      return [...current, tenant];
    });
  }

  async function create(proof: StepUpProof, payload: BatchPayload) {
    const key = idempotency.keyFor(AGENT_WORK_ORDERS_BATCH_URL, { tenantIds: payload.tenantIds, ...payload.form });
    let created: AgentWorkOrderDto[];
    try {
      created = await createAgentWorkOrderBatch(payload.tenantIds, payload.form, proof, key);
      idempotency.settle();
    } catch (err) {
      idempotency.settle(err);
      throw err;
    }
    setNotice(`${created.length} ordem(ns) de serviço criada(s): ${kindLabel(payload.form.kind)}.`);
    setSelected([]);
    if (options) setForm(minimumForm(options, payload.form.kind));
    await loadRecent();
  }

  const stepUp = useStepUpDialog(create, (err) => setError(describeWorkOrderError(err)), {
    isValid: (payload) =>
      options !== null && payload.tenantIds.length >= 1 && payload.tenantIds.length <= maxBatch && isFormValid(payload.form, options),
    passkeyTarget: (payload) =>
      payload.form.agentClientId ? stepUpTarget('agentWorkOrderCreateBatch', payload.form.agentClientId) : null,
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!form || !options || selected.length === 0 || !isFormValid(form, options) || stepUp.open) return;
    setError(null);
    setNotice(null);
    stepUp.start({ tenantIds: selected.map((t) => t.id), form: { ...form } });
  }

  async function cancel(item: AgentWorkOrderDto) {
    if (!window.confirm(`Cancelar a ordem de serviço ${item.id}? O agente deixa de poder usá-la.`)) return;
    setError(null);
    setNotice(null);
    setCancelling(item.id);
    try {
      await cancelAgentWorkOrder(item.id);
      setNotice(`Ordem de serviço ${item.id} cancelada.`);
      await loadRecent();
    } catch (err) {
      setError(describeWorkOrderError(err));
    } finally {
      setCancelling(null);
    }
  }

  if (!options) {
    return <div className="page">{error ? <div className="error-banner">{error}</div> : <p>Carregando…</p>}</div>;
  }

  const canSubmit = form !== null && selected.length > 0 && isFormValid(form, options);

  return (
    <div className="page" style={{ maxWidth: 1100 }}>
      {error && <div className="error-banner" role="alert">{error}</div>}
      {notice && <div className="info-banner" role="status">{notice}</div>}

      <div className="card">
        <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Lojas</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Marque até {maxBatch} lojas. Cada uma recebe a sua própria ordem de serviço, com os mesmos máximos.
        </p>
        <fieldset disabled={stepUp.open} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          <div className="grid-3" style={{ alignItems: 'end' }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor="batch-q">Buscar por nome, slug ou telefone</label>
              <input id="batch-q" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor="batch-status">Assinatura</label>
              <select id="batch-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>
          {tenants.length === 0 ? (
            <p className="muted">Nenhuma loja encontrada.</p>
          ) : (
            <div className="table-scroll">
              <CardTable className="table" aria-label="Lojas para agir">
                <THead>
                  <Tr>
                    <Th />
                    <Th>Loja</Th>
                    <Th>Assinatura</Th>
                    <Th>Teste grátis até</Th>
                  </Tr>
                </THead>
                <TBody>
                  {tenants.map((t) => {
                    const checked = selected.some((s) => s.id === t.id);
                    return (
                      <Tr key={t.id}>
                        <Td action>
                          <input
                            type="checkbox"
                            aria-label={`Marcar ${t.tradeName}`}
                            checked={checked}
                            disabled={!checked && selected.length >= maxBatch}
                            onChange={() => toggle(t)}
                          />
                        </Td>
                        <Td heading>
                          <Link to={`/tenants/${t.id}`}>{t.tradeName}</Link> <span className="mono muted">{t.slug}</span>
                        </Td>
                        <Td label="Assinatura">{subscriptionStatusLabel(t.subscriptionStatus)}</Td>
                        <Td label="Teste grátis até">{t.trialExpiresAt ? new Date(t.trialExpiresAt).toLocaleDateString('pt-BR') : '—'}</Td>
                      </Tr>
                    );
                  })}
                </TBody>
              </CardTable>
            </div>
          )}
        </fieldset>
      </div>

      <form className="card" onSubmit={submit} aria-label="Ordens de serviço em lote">
        <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>
          Delegar ao agente ({selected.length} de {maxBatch})
        </h3>
        {selected.length > 0 && (
          <p data-testid="batch-selection">{selected.map((t) => t.tradeName).join(', ')}</p>
        )}
        {options.kinds.length === 0 ? (
          <div className="info-banner">Seu papel não tem permissão para nenhum tipo de ordem de serviço em lote.</div>
        ) : form && (
          <>
            <AgentWorkOrderFields options={options} form={form} onChange={setForm} batch disabled={stepUp.open} />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="submit" className="btn" disabled={!canSubmit || stepUp.open}>
                Delegar ao agente
              </button>
            </div>
          </>
        )}
      </form>

      <div className="card">
        <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Ordens de serviço recentes</h3>
        <AgentWorkOrderTable items={recent} showTenant onCancel={(item) => void cancel(item)} busyId={cancelling} />
      </div>

      {stepUp.open && form && (
        <StepUpDialog
          title="Confirmar as ordens de serviço"
          description={
            <>
              {kindLabel(form.kind)} em <strong>{selected.length} loja(s)</strong>, até {form.maxDays} dia(s) e{' '}
              {form.maxEffects} efeito(s) cada, válidas por {form.validForHours} h.
            </>
          }
          confirmLabel="Delegar"
          busy={stepUp.busy}
          error={stepUp.error}
          onSubmit={stepUp.submit}
          onCancel={stepUp.cancel}
          onPasskey={stepUp.passkeyAvailable ? stepUp.submitPasskey : undefined}
        />
      )}
    </div>
  );
}
